import { useEffect, useId, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useSelector } from 'react-redux';
import CustomerAvatar from './CustomerAvatar';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { useCustomerPhoto } from '@/hooks/useCustomerPhoto';
import { useRemoveCustomerPhotoMutation, useUpdateCustomerPhotoMutation } from '@/slices/customerSlice';

type Customer = {
  _id: string;
  _business?: string | { _id: string };
  name?: string;
  photoUrl?: string | null;
};

const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

type CustomerPhotoEditorProps = {
  customer: Customer;
  className?: string;
  fallbackClassName?: string;
};

// Key the controls by customer so an unfinished operation never changes a
// different customer's dialog or announcements when navigating the inbox.
export function CustomerPhotoEditor({ customer, className, fallbackClassName }: CustomerPhotoEditorProps) {
  const business = typeof customer._business === 'string' ? customer._business : customer._business?._id;
  return <CustomerPhotoControls key={`${business || ''}:${customer._id}`} customer={customer} className={className} fallbackClassName={fallbackClassName} />;
}

function CustomerPhotoControls({ customer, className, fallbackClassName }: CustomerPhotoEditorProps) {
  const currentCustomer = useCustomerPhoto(customer)!;
  const activeBusiness = useSelector((state: any) => state.auth?._id);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<'saving' | 'removing' | null>(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const mounted = useRef(true);
  const pending = useRef(false);
  const helpId = useId();
  const [uploadPhoto] = useUpdateCustomerPhotoMutation();
  const [removePhoto] = useRemoveCustomerPhotoMutation();
  const name = customer.name || 'customer';

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  async function save(file?: File) {
    if (pending.current) return;
    setError('');
    setStatus('');
    if (file && (!PHOTO_TYPES.includes(file.type) || file.size === 0)) {
      setError('Choose a JPG, PNG or WebP image.');
      return;
    }
    if (file && file.size > MAX_PHOTO_BYTES) {
      setError('Choose a photo smaller than 5 MB.');
      return;
    }
    pending.current = true;
    setBusy(file ? 'saving' : 'removing');
    const _business = typeof customer._business === 'string'
      ? customer._business : customer._business?._id || activeBusiness;
    try {
      const args = { _customer: customer._id, _business, ...(file ? { file } : {}) };
      await (file ? uploadPhoto(args) : removePhoto(args)).unwrap();
      if (mounted.current) setStatus(file ? 'Photo saved.' : 'Photo removed.');
    } catch (requestError: any) {
      if (mounted.current) setError(requestError?.data?.message || 'The photo could not be saved. Please try again.');
    } finally {
      pending.current = false;
      if (mounted.current) setBusy(null);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => {
      setOpen(nextOpen);
      if (nextOpen) { setError(''); setStatus(''); }
    }}>
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label={`Edit photo for ${name}`}
          title="Edit customer photo"
          className="shrink-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <CustomerAvatar customer={currentCustomer} className={className || 'h-12 w-12'} fallbackClassName={fallbackClassName} />
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Customer photo</DialogTitle>
          <DialogDescription>Choose a photo for {name}.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4 py-2" aria-busy={!!busy}>
          <CustomerAvatar customer={currentCustomer} className="h-24 w-24 text-2xl" />
          <p id={helpId} className="text-sm text-muted-foreground">JPG, PNG or WebP. Up to 5 MB.</p>
          <input
            ref={input}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            aria-label="Customer photo file"
            aria-describedby={helpId}
            disabled={!!busy}
            onChange={(event) => {
              const file = event.currentTarget.files?.[0];
              event.currentTarget.value = '';
              if (file) void save(file);
            }}
          />
          <div className="flex flex-wrap justify-center gap-2">
            <Button type="button" disabled={!!busy} aria-describedby={helpId} onClick={() => input.current?.click()}>
              {busy === 'saving' && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
              {currentCustomer.photoUrl ? 'Change photo' : 'Add photo'}
            </Button>
            {currentCustomer.photoUrl && (
              <Button type="button" variant="outline" disabled={!!busy} onClick={() => void save()}>
                {busy === 'removing' && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
                Remove photo
              </Button>
            )}
          </div>
          {error && <p role="alert" className="text-sm text-destructive text-center">{error}</p>}
          <p role="status" className="text-sm text-muted-foreground text-center">
            {busy ? (busy === 'saving' ? 'Saving photo…' : 'Removing photo…') : status}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
